import { createAction, createAsyncThunk, type Dispatch } from '@reduxjs/toolkit';

import { LIMITE_DO_HISTORICO, TAMANHO_DA_PAGINA } from '../constants/centralWebhooks';
import type {
  AlteracaoDaIntegracao,
  AlvoDoReenvio,
  AmbienteDoDestino,
  DesfechoDoReenvio,
  DestinoDoWebhook,
  DetalheDoEvento,
  EdicaoDoDestino,
  ErroDaCentral,
  EscolhaDeEventos,
  EventoDoWebhook,
  FiltroDosEventos,
  IntegracaoDeWebhook,
  LeituraDaCentral,
  Pagina,
  PedidoDeReenvio,
  ResultadoDaRetomada,
  ResultadoDoSegredo,
  ResultadoDoTeste,
} from '../types/centralWebhooksTypes';
import { centralWebhooksService, FalhaDaCentral } from './services';

interface Config {
  rejectValue: ErroDaCentral;
}

/**
 * Falha de rede e "Sessão expirada" do requestBuilder chegam como `Error` comum: status 0 separa
 * "nem chegou ao core" de uma recusa do core.
 */
const paraErroDaCentral = (erro: unknown): ErroDaCentral =>
  erro instanceof FalhaDaCentral
    ? erro.erro
    : {
        status: 0,
        codigo: null,
        mensagem: 'Não foi possível falar com o servidor. Verifique a conexão e tente novamente.',
      };

interface NoDestino {
  escritorioId: string;
  ambiente: AmbienteDoDestino;
}

export const lerCentralDeWebhooks = createAsyncThunk<
  LeituraDaCentral & { escritorioId: string },
  string,
  Config
>('centralWebhooks/ler', async (escritorioId, { rejectWithValue }) => {
  try {
    return { escritorioId, ...(await centralWebhooksService.ler(escritorioId)) };
  } catch (erro) {
    return rejectWithValue(paraErroDaCentral(erro));
  }
});

export const criarIntegracaoDeWebhook = createAsyncThunk<
  IntegracaoDeWebhook,
  { escritorioId: string; escolha: EscolhaDeEventos },
  Config
>('centralWebhooks/criar', async ({ escritorioId, escolha }, { rejectWithValue }) => {
  try {
    return await centralWebhooksService.criarIntegracao(escritorioId, escolha);
  } catch (erro) {
    return rejectWithValue(paraErroDaCentral(erro));
  }
});

export const salvarEventosDoWebhook = createAsyncThunk<
  IntegracaoDeWebhook,
  { escritorioId: string; escolha: EscolhaDeEventos },
  Config
>('centralWebhooks/salvarEventos', async ({ escritorioId, escolha }, { rejectWithValue }) => {
  try {
    return await centralWebhooksService.salvarEventos(escritorioId, escolha);
  } catch (erro) {
    return rejectWithValue(paraErroDaCentral(erro));
  }
});

export const editarDestinoDoWebhook = createAsyncThunk<
  DestinoDoWebhook,
  NoDestino & { edicao: EdicaoDoDestino },
  Config
>('centralWebhooks/editar', async ({ escritorioId, ambiente, edicao }, { rejectWithValue }) => {
  try {
    return await centralWebhooksService.editarDestino(escritorioId, ambiente, edicao);
  } catch (erro) {
    return rejectWithValue(paraErroDaCentral(erro));
  }
});

export const enviarTesteDoWebhook = createAsyncThunk<ResultadoDoTeste, NoDestino, Config>(
  'centralWebhooks/teste',
  async ({ escritorioId, ambiente }, { rejectWithValue }) => {
    try {
      return await centralWebhooksService.enviarTeste(escritorioId, ambiente);
    } catch (erro) {
      return rejectWithValue(paraErroDaCentral(erro));
    }
  },
);

export const ativarDestinoDoWebhook = createAsyncThunk<DestinoDoWebhook, NoDestino, Config>(
  'centralWebhooks/ativar',
  async ({ escritorioId, ambiente }, { rejectWithValue }) => {
    try {
      return await centralWebhooksService.ativar(escritorioId, ambiente);
    } catch (erro) {
      return rejectWithValue(paraErroDaCentral(erro));
    }
  },
);

export const pausarDestinoDoWebhook = createAsyncThunk<DestinoDoWebhook, NoDestino, Config>(
  'centralWebhooks/pausar',
  async ({ escritorioId, ambiente }, { rejectWithValue }) => {
    try {
      return await centralWebhooksService.pausar(escritorioId, ambiente);
    } catch (erro) {
      return rejectWithValue(paraErroDaCentral(erro));
    }
  },
);

export const retomarDestinoDoWebhook = createAsyncThunk<
  ResultadoDaRetomada & { destino: DestinoDoWebhook },
  NoDestino,
  Config
>('centralWebhooks/retomar', async ({ escritorioId, ambiente }, { rejectWithValue }) => {
  try {
    return await centralWebhooksService.retomar(escritorioId, ambiente);
  } catch (erro) {
    return rejectWithValue(paraErroDaCentral(erro));
  }
});

export const segredoIniciado = createAction<AmbienteDoDestino>('centralWebhooks/segredoIniciado');
export const segredoConcluido = createAction<{ escritorioId: string; destino: DestinoDoWebhook }>(
  'centralWebhooks/segredoConcluido',
);
export const segredoRecusado = createAction<{
  escritorioId: string;
  ambiente: AmbienteDoDestino;
  erro: ErroDaCentral;
}>('centralWebhooks/segredoRecusado');

/**
 * Gerar/rotacionar não usa `createAsyncThunk`, pela mesma razão das chaves de API: ele põe o
 * retorno no `payload` do `fulfilled`, e o Redux DevTools guarda toda ação despachada. O segredo vai
 * só a quem despachou, pelo retorno do `dispatch`; as ações levam o destino sem segredo e o erro.
 */
export const gerarSegredoDoWebhook =
  ({ escritorioId, ambiente }: NoDestino) =>
  async (dispatch: Dispatch): Promise<ResultadoDoSegredo> => {
    dispatch(segredoIniciado(ambiente));
    try {
      const { destino, segredo } = await centralWebhooksService.gerarSegredo(
        escritorioId,
        ambiente,
      );
      dispatch(segredoConcluido({ escritorioId, destino }));
      return { segredo, erro: null };
    } catch (erro) {
      const falha = paraErroDaCentral(erro);
      dispatch(segredoRecusado({ escritorioId, ambiente, erro: falha }));
      return { segredo: null, erro: falha };
    }
  };

export const listarEventosDoWebhook = createAsyncThunk<
  Pagina<EventoDoWebhook>,
  { escritorioId: string; filtro: FiltroDosEventos; cursor: string | null },
  Config
>(
  'centralWebhooks/listarEventos',
  async ({ escritorioId, filtro, cursor }, { rejectWithValue }) => {
    try {
      return await centralWebhooksService.listarEventos(
        escritorioId,
        filtro,
        cursor,
        TAMANHO_DA_PAGINA,
      );
    } catch (erro) {
      return rejectWithValue(paraErroDaCentral(erro));
    }
  },
);

export const lerEventoDoWebhook = createAsyncThunk<
  DetalheDoEvento,
  { escritorioId: string; eventoId: string },
  Config
>('centralWebhooks/lerEvento', async ({ escritorioId, eventoId }, { rejectWithValue }) => {
  try {
    return await centralWebhooksService.lerEvento(escritorioId, eventoId);
  } catch (erro) {
    return rejectWithValue(paraErroDaCentral(erro));
  }
});

const pedidoDoAlvo = (alvo: AlvoDoReenvio): PedidoDeReenvio =>
  alvo.tipo === 'evento' ? { eventoIds: [alvo.eventoId] } : { destinoId: alvo.destinoId };

export const reenviarEventosDoWebhook = createAsyncThunk<
  DesfechoDoReenvio,
  { escritorioId: string; alvo: AlvoDoReenvio },
  Config
>('centralWebhooks/reenviar', async ({ escritorioId, alvo }, { rejectWithValue }) => {
  try {
    return await centralWebhooksService.reenviar(escritorioId, pedidoDoAlvo(alvo));
  } catch (erro) {
    return rejectWithValue(paraErroDaCentral(erro));
  }
});

export const listarHistoricoDoWebhook = createAsyncThunk<AlteracaoDaIntegracao[], string, Config>(
  'centralWebhooks/listarHistorico',
  async (escritorioId, { rejectWithValue }) => {
    try {
      return await centralWebhooksService.listarHistorico(escritorioId, LIMITE_DO_HISTORICO);
    } catch (erro) {
      return rejectWithValue(paraErroDaCentral(erro));
    }
  },
);
